// From the shadcn/ui dark mode guide for Vite (MIT):
// https://ui.shadcn.com/docs/dark-mode/vite
import { createContext, useContext, useLayoutEffect, useState } from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  // A page that looks one way whatever the user's theme pins it while it's
  // mounted (usePinnedTheme): the report's paper is white
  pin: (theme: "light" | "dark" | null) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  pin: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )
  const [pinned, pin] = useState<"light" | "dark" | null>(null)

  // Before the browser paints, so a pinned page never shows the other theme
  useLayoutEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")

    if (pinned) {
      root.classList.add(pinned)
      return
    }

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme, pinned])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
    pin,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}

// Keeps the whole page in one theme while the component is mounted, then
// gives the user's back
export function usePinnedTheme(theme: "light" | "dark") {
  const { pin } = useTheme()
  useLayoutEffect(() => {
    pin(theme)
    return () => pin(null)
  }, [pin, theme])
}
